import React from "react";
import { useAppSelector } from "../../../store/hooks";
import { calculateModuleCost } from "../../../utils/validation/moduleValidation";


/**
 * Module Calculations Step Component
 * Handles reviewing module statistics and calculations
 */
export const ModuleCalculationsStep: React.FC = () => {

  const moduleConfig = useAppSelector((state) => state.module.moduleConfig);



  if (!moduleConfig) {
    return <div className="text-gray-600 dark:text-gray-300">Loading module configuration...</div>;
  }


  
  // Create detailed calculations for display
  const baseModuleCost = calculateModuleCost(moduleConfig.size);
  
  // Calculate systems cost and tonnage (including all systems, not just optional)
  const calculateSystemsTonnage = () => {
    let totalUsed = 0;
    
    // Core systems
    if (moduleConfig.systems?.bridge) totalUsed += moduleConfig.systems.bridge.tonnage || 0;
    if (moduleConfig.systems?.computer?.length) {
      moduleConfig.systems.computer.forEach(comp => {
        totalUsed += comp.tonnage || 0;
      });
    }
    if (moduleConfig.systems?.sensors) totalUsed += moduleConfig.systems.sensors.tonnage || 0;
    if (moduleConfig.systems?.powerPlant?.length) {
      moduleConfig.systems.powerPlant.forEach(plant => {
        totalUsed += plant.tonnage || 0;
      });
    }
    if (moduleConfig.systems?.drives?.length) {
      moduleConfig.systems.drives.forEach(drive => {
        totalUsed += drive.tonnage || 0;
      });
    }
    if (moduleConfig.systems?.fuel) totalUsed += moduleConfig.systems.fuel.tonnage || 0;
    if (moduleConfig.systems?.staterooms?.length) {
      moduleConfig.systems.staterooms.forEach(stateroom => {
        totalUsed += stateroom.tonnage || 0;
      });
    }
    if (moduleConfig.systems?.weapons?.length) {
      moduleConfig.systems.weapons.forEach(weapon => {
        totalUsed += weapon.tonnage || 0;
      });
    }
    
    // Optional systems
    if (moduleConfig.systems?.optionalSystems?.length) {
      moduleConfig.systems.optionalSystems.forEach(system => {
        totalUsed += system.tonnage || 0;
      });
    }
    
    return totalUsed;
  };

  const calculateSystemsCost = () => {
    let totalCost = 0;
    
    // Core systems
    if (moduleConfig.systems?.bridge) totalCost += moduleConfig.systems.bridge.cost || 0;
    if (moduleConfig.systems?.computer?.length) {
      moduleConfig.systems.computer.forEach(comp => {
        totalCost += comp.cost || 0;
      });
    }
    if (moduleConfig.systems?.sensors) totalCost += moduleConfig.systems.sensors.cost || 0;
    if (moduleConfig.systems?.powerPlant?.length) {
      moduleConfig.systems.powerPlant.forEach(plant => {
        totalCost += plant.cost || 0;
      });
    }
    if (moduleConfig.systems?.drives?.length) {
      moduleConfig.systems.drives.forEach(drive => {
        totalCost += drive.cost || 0;
      });
    }
    if (moduleConfig.systems?.fuel) totalCost += moduleConfig.systems.fuel.cost || 0;
    if (moduleConfig.systems?.staterooms?.length) {
      moduleConfig.systems.staterooms.forEach(stateroom => {
        totalCost += stateroom.cost || 0;
      });
    }
    if (moduleConfig.systems?.weapons?.length) {
      moduleConfig.systems.weapons.forEach(weapon => {
        totalCost += weapon.cost || 0;
      });
    }
    
    // Optional systems
    if (moduleConfig.systems?.optionalSystems?.length) {
      moduleConfig.systems.optionalSystems.forEach(system => {
        totalCost += system.cost || 0;
      });
    }
    
    return totalCost;
  };

  const calculateSystemsPower = () => {
    let totalPower = 0;
    
    // Core systems
    if (moduleConfig.systems?.bridge) totalPower += moduleConfig.systems.bridge.power || 0;
    if (moduleConfig.systems?.computer?.length) {
      moduleConfig.systems.computer.forEach(comp => {
        totalPower += comp.power || 0;
      });
    }
    if (moduleConfig.systems?.sensors) totalPower += moduleConfig.systems.sensors.power || 0;
    if (moduleConfig.systems?.powerPlant?.length) {
      moduleConfig.systems.powerPlant.forEach(plant => {
        totalPower += plant.power || 0;
      });
    }
    if (moduleConfig.systems?.drives?.length) {
      moduleConfig.systems.drives.forEach(drive => {
        totalPower += drive.power || 0;
      });
    }
    if (moduleConfig.systems?.fuel) totalPower += moduleConfig.systems.fuel.power || 0;
    if (moduleConfig.systems?.staterooms?.length) {
      moduleConfig.systems.staterooms.forEach(stateroom => {
        totalPower += stateroom.power || 0;
      });
    }
    if (moduleConfig.systems?.weapons?.length) {
      moduleConfig.systems.weapons.forEach(weapon => {
        totalPower += weapon.power || 0;
      });
    }
    
    // Optional systems
    if (moduleConfig.systems?.optionalSystems?.length) {
      moduleConfig.systems.optionalSystems.forEach(system => {
        totalPower += system.power || 0;
      });
    }
    
    return totalPower;
  };

  const systemsCost = calculateSystemsCost();
  const systemsTons = calculateSystemsTonnage();
  const systemsPower = Math.round(calculateSystemsPower() * 100) / 100;
  const equipmentCost = 0;
  const equipmentTons = 0;
  const equipmentPower = 0;
  const totalCost = baseModuleCost + systemsCost + equipmentCost;
  const totalTons = systemsTons + equipmentTons;
  const totalPower = Math.round((systemsPower + equipmentPower) * 100) / 100;
  const remainingTons = moduleConfig.size - totalTons;
  const costPerTon = totalCost / moduleConfig.size;
  const systemsCount = (moduleConfig.systems?.optionalSystems?.length || 0) + 
                      (moduleConfig.systems?.computer?.length || 0) + 
                      (moduleConfig.systems?.sensors ? 1 : 0) + 
                      (moduleConfig.systems?.powerPlant?.length || 0) + 
                      (moduleConfig.systems?.drives?.length || 0) + 
                      (moduleConfig.systems?.fuel ? 1 : 0) + 
                      (moduleConfig.systems?.staterooms?.length || 0) + 
                      (moduleConfig.systems?.weapons?.length || 0);
  const equipmentCount = 0;
  
  const calculations = {
    baseModuleCost,
    systemsCost,
    equipmentCost,
    totalCost,
    systemsTons,
    equipmentTons,
    remainingTons,
    systemsPower,
    equipmentPower,
    totalPower,
    costPerTon,
    systemsCount,
    equipmentCount,
  };

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString();
  };

  const formatPercentage = (value: number, total: number) => {
    if (total === 0) return "0%";
    return `${((value / total) * 100).toFixed(1)}%`;
  };



  return (
    <div className="space-y-6">
      {/* Module Overview */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 border border-blue-200 dark:border-blue-700 p-6 rounded-lg">
        <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">📋 Module Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{moduleConfig.name || "Unnamed Module"}</div>
            <div className="text-sm text-gray-600 dark:text-gray-300">Module Name</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{moduleConfig.size} tons</div>
            <div className="text-sm text-gray-600 dark:text-gray-300">Total Size</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">TL {moduleConfig.techLevel}</div>
            <div className="text-sm text-gray-600 dark:text-gray-300">Tech Level</div>
          </div>
        </div>
      </div>

      {/* Cost Breakdown */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">💰 Detailed Cost Breakdown</h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-gray-700 dark:text-gray-300">Base Module Cost ({moduleConfig.size} tons × Cr25,000/ton)</span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(calculations.baseModuleCost)} Cr</span>
          </div>
          
          <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-gray-700 dark:text-gray-300">Optional Systems Cost</span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(calculations.systemsCost)} Cr</span>
          </div>
          
          <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-gray-700 dark:text-gray-300">Additional Equipment Cost</span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(calculations.equipmentCost)} Cr</span>
          </div>
          
          <div className="flex justify-between items-center py-3 bg-gray-50 dark:bg-gray-700 rounded font-bold text-lg">
            <span className="text-gray-900 dark:text-gray-100">Total Module Cost</span>
            <span className="text-green-600 dark:text-green-400">{formatCurrency(calculations.totalCost)} Cr</span>
          </div>
        </div>
      </div>

      {/* Tonnage Analysis */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">⚖️ Tonnage Analysis</h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-gray-700 dark:text-gray-300">Total Module Size</span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">{moduleConfig.size} tons</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-gray-700 dark:text-gray-300">Used by Systems</span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">{calculations.systemsTons} tons ({formatPercentage(calculations.systemsTons, moduleConfig.size)})</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-gray-700 dark:text-gray-300">Used by Equipment</span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">{calculations.equipmentTons} tons ({formatPercentage(calculations.equipmentTons, moduleConfig.size)})</span>
          </div>
          
          <div className="flex justify-between items-center py-2 bg-gray-50 dark:bg-gray-700 rounded px-3">
            <span className="font-medium text-gray-900 dark:text-gray-100">Remaining Available</span>
            <span className={`font-bold ${calculations.remainingTons >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {calculations.remainingTons} tons ({formatPercentage(calculations.remainingTons, moduleConfig.size)})
            </span>
          </div>
        </div>
      </div>

      {/* Power Requirements */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">⚡ Power Requirements</h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-gray-700 dark:text-gray-300">Systems Power Draw</span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">{Math.round(calculations.systemsPower * 100) / 100} Power</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-gray-700 dark:text-gray-300">Equipment Power Draw</span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">{Math.round(calculations.equipmentPower * 100) / 100} Power</span>
          </div>
          
          <div className="flex justify-between items-center py-2 bg-gray-50 dark:bg-gray-700 rounded px-3">
            <span className="font-medium text-gray-900 dark:text-gray-100">Total Power Required</span>
            <span className="font-bold text-blue-600 dark:text-blue-400">{Math.round(calculations.totalPower * 100) / 100} Power</span>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">📊 Performance Metrics</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/30 rounded">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{calculations.costPerTon.toFixed(0)}</div>
            <div className="text-sm text-gray-600 dark:text-gray-300">Cr/ton</div>
          </div>
          <div className="text-center p-3 bg-green-50 dark:bg-green-900/30 rounded">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{formatPercentage(calculations.systemsTons, moduleConfig.size)}</div>
            <div className="text-sm text-gray-600 dark:text-gray-300">Space Used</div>
          </div>
          <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/30 rounded">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{calculations.systemsCount}</div>
            <div className="text-sm text-gray-600 dark:text-gray-300">Systems</div>
          </div>
          <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/30 rounded">
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{calculations.equipmentCount}</div>
            <div className="text-sm text-gray-600 dark:text-gray-300">Equipment</div>
          </div>
        </div>
      </div>

      {/* Validation Status */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">✅ Validation Status</h3>
        <div className="space-y-3">
          <div className="flex items-center">
            <div className={`w-4 h-4 rounded-full mr-3 ${calculations.remainingTons >= 0 ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className={calculations.remainingTons >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}>
              {calculations.remainingTons >= 0 
                ? `✓ Tonnage within limits (${calculations.remainingTons} tons remaining)`
                : `✗ Tonnage exceeded by ${Math.abs(calculations.remainingTons)} tons`
              }
            </span>
          </div>
          
          <div className="flex items-center">
            <div className="w-4 h-4 rounded-full mr-3 bg-green-500"></div>
            <span className="text-green-700 dark:text-green-400">
              ✓ All systems meet technology level requirements (TL {moduleConfig.techLevel})
            </span>
          </div>
          
          <div className="flex items-center">
            <div className="w-4 h-4 rounded-full mr-3 bg-green-500"></div>
            <span className="text-green-700 dark:text-green-400">
              ✓ No non-modular systems detected (bridge, drives)
            </span>
          </div>
          

        </div>
      </div>


    </div>
  );
};


